import { INDIAN_GST_STATES, stateNameByCode } from '../constants/indianGstStates';

export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/;
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export const GST_STATE_OPTIONS = INDIAN_GST_STATES.map((state) => ({
  value: state.code,
  label: `${state.code} - ${state.name}`,
}));

/** Parse GSTIN for state code (first 2 digits) and PAN (chars 3–12). */
export function parseGstin(raw) {
  const gstin = String(raw || '').toUpperCase().replace(/\s/g, '');
  const stateCode = gstin.slice(0, 2);
  const pan = gstin.length >= 12 ? gstin.slice(2, 12) : '';
  const knownState = /^\d{2}$/.test(stateCode) && Boolean(stateNameByCode(stateCode));

  return {
    gstin,
    valid: GSTIN_REGEX.test(gstin),
    stateCode: knownState ? stateCode : '',
    stateName: knownState ? stateNameByCode(stateCode) : '',
    pan: PAN_REGEX.test(pan) ? pan : '',
  };
}

/** Fields to merge when the user types a GSTIN (state + PAN autofill). */
export function gstinSideEffects(gstin, current = {}) {
  const parsed = parseGstin(gstin);
  const next = { gst_no: parsed.gstin };

  if (parsed.stateCode) {
    next.pump_state_code = parsed.stateCode;
    next.pump_state_name = parsed.stateName;
    next.state_code = parsed.stateCode;
    next.place_supply_code = parsed.stateCode;
    next.place = parsed.stateName;
    if (current.pump_state_code && current.pump_state_code !== parsed.stateCode) {
      next.pump_district = '';
    }
  }

  // Always auto-fix PAN from GSTIN chars 3–12 once a valid PAN shape is present.
  if (parsed.pan) {
    next.pan_no = parsed.pan;
  }

  return next;
}

/** True when PAN was (or can be) derived from the current GSTIN. */
export function isPanAutoFromGstin(values = {}) {
  const parsed = parseGstin(values.gst_no);
  return Boolean(parsed.pan);
}

/**
 * Merge a single field or a patch object into invoice form state.
 * Supports onChange(name, value) and onChange({ field: value, ... }).
 */
export function applyInvoiceFieldUpdates(prev, nameOrPatch, value) {
  if (nameOrPatch && typeof nameOrPatch === 'object' && !Array.isArray(nameOrPatch)) {
    return { ...prev, ...nameOrPatch };
  }
  return { ...prev, [nameOrPatch]: value };
}

/** Ensure PAN (and uppercase GSTIN) stay in sync before validate/save. */
export function syncInvoiceGstFields(values = {}) {
  const parsed = parseGstin(values.gst_no);
  return {
    ...values,
    gst_no: parsed.gstin,
    pan_no: (parsed.pan || values.pan_no || '').toUpperCase(),
  };
}

export function applyStateSelection(stateCode) {
  const name = stateNameByCode(stateCode) || '';
  return {
    pump_state_code: stateCode || '',
    pump_state_name: name,
    pump_district: '',
    state_code: stateCode || '',
    place_supply_code: stateCode || '',
    place: name,
  };
}

/** Payload for invoice-settings/save from the subscription GST form. */
export function buildGstSavePayload(values) {
  const gstEnabled = values.invoice_gst_enabled !== false && values.invoice_gst_enabled !== '0';
  const stateCode = values.pump_state_code || '';
  const stateName = values.pump_state_name || stateNameByCode(stateCode) || '';
  const address = (values.invoice_address || values.pump_address || '').trim();
  const parsed = parseGstin(values.gst_no);

  return {
    ...values,
    invoice_gst_enabled: gstEnabled,
    gst_no: parsed.gstin,
    // GSTIN always wins for PAN when chars 3–12 form a valid PAN.
    pan_no: (parsed.pan || values.pan_no || '').toUpperCase(),
    dealer: (values.dealer || '').trim(),
    invoice_address: address,
    pump_address: (values.pump_address || address).trim(),
    pump_state_code: stateCode,
    pump_state_name: stateName,
    pump_district: (values.pump_district || '').trim(),
    state_code: stateCode,
    place_supply_code: stateCode,
    place: stateName,
  };
}

export const DEFAULT_INVOICE_SETTINGS = {
  pump_address: '',
  pump_email: '',
  pump_phone: '',
  pump_state_code: '',
  pump_state_name: '',
  pump_district: '',
  latitude: '',
  longitude: '',
  invoice_address: '',
  gst_no: '',
  dealer: '',
  place: '',
  place_supply_code: '',
  bill_no: '',
  invoice_gst_enabled: true,
  state_code: '',
  pan_no: '',
};

export function mapInvoiceSettingsFromApi(data = {}) {
  const gstNo = (data.gst_no || '').toUpperCase();
  const parsed = parseGstin(gstNo);
  return {
    pump_address: data.pump_address || '',
    pump_email: data.pump_email || '',
    pump_phone: data.pump_phone || '',
    pump_state_code: data.pump_state_code || '',
    pump_state_name: data.pump_state_name || '',
    pump_district: data.pump_district || '',
    latitude: data.latitude ?? '',
    longitude: data.longitude ?? '',
    invoice_address: data.invoice_address || '',
    gst_no: gstNo,
    dealer: data.dealer || '',
    place: data.place || '',
    place_supply_code: data.place_supply_code || '',
    bill_no: data.bill_no || '',
    invoice_gst_enabled: data.invoice_gst_enabled !== false && data.invoice_gst_enabled !== '0',
    state_code: data.state_code || data.pump_state_code || '',
    pan_no: (data.pan_no || parsed.pan || '').toUpperCase(),
    tax_type_hint: data.tax_type_hint || '',
  };
}

export function validateInvoiceSettings(values) {
  const err = {};

  if (!values.invoice_address?.trim()) {
    err.invoice_address = 'Invoice address is required';
  } else if (values.invoice_address.trim().length < 5) {
    err.invoice_address = 'Invoice address must be at least 5 characters';
  }

  if (!values.dealer?.trim()) {
    err.dealer = 'Dealer name is required';
  }

  if (!values.place_supply_code?.trim()) {
    err.place_supply_code = 'Place of supply is required';
  }

  if (!values.pump_state_code?.trim()) {
    err.pump_state_code = 'Pump state is required';
  }

  if (values.invoice_gst_enabled) {
    if (!values.gst_no?.trim()) {
      err.gst_no = 'GST number is required when GST invoicing is enabled';
    } else if (!GSTIN_REGEX.test(values.gst_no.trim().toUpperCase())) {
      err.gst_no = 'Enter valid GST number format';
    }
  }

  if (values.pan_no?.trim() && !PAN_REGEX.test(values.pan_no.trim().toUpperCase())) {
    err.pan_no = 'Enter valid PAN format';
  }

  if (values.latitude !== '' && values.latitude != null) {
    const lat = Number(values.latitude);
    if (Number.isNaN(lat) || lat < -90 || lat > 90) {
      err.latitude = 'Latitude must be between -90 and 90';
    }
  }

  if (values.longitude !== '' && values.longitude != null) {
    const lng = Number(values.longitude);
    if (Number.isNaN(lng) || lng < -180 || lng > 180) {
      err.longitude = 'Longitude must be between -180 and 180';
    }
  }

  return err;
}

/** Subscription GST tab: legal name, GSTIN, state, district, address. */
export function validateGstProfile(values) {
  const err = {};
  const gstEnabled = values.invoice_gst_enabled !== false && values.invoice_gst_enabled !== '0';

  if (!values.dealer?.trim()) {
    err.dealer = 'Legal / trade name is required';
  }

  const address = (values.invoice_address || values.pump_address || '').trim();
  if (!address) {
    err.invoice_address = 'Billing address is required';
  } else if (address.length < 5) {
    err.invoice_address = 'Billing address must be at least 5 characters';
  }

  if (!values.pump_state_code?.trim()) {
    err.pump_state_code = 'State is required';
  }

  if (!values.pump_district?.trim()) {
    err.pump_district = 'District is required';
  }

  if (gstEnabled) {
    if (!values.gst_no?.trim()) {
      err.gst_no = 'GSTIN is required';
    } else if (!GSTIN_REGEX.test(values.gst_no.trim().toUpperCase())) {
      err.gst_no = 'Enter a valid 15-character GSTIN';
    }
  }

  if (values.pan_no?.trim() && !PAN_REGEX.test(values.pan_no.trim().toUpperCase())) {
    err.pan_no = 'Enter valid PAN format';
  }

  return err;
}

export { INDIAN_GST_STATES, stateNameByCode };
